import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../components/profile-components/Navbar";
import Topbar from "../../components/profile-components/Topbar";
import API_BASE_URL from "../../config";
import usePageTitle from "../../hooks/usePageTitle";
import "../SalesPages.css";

const emptyForm = {
  label: "",
  field_type: "text",
  applies_to_leads: false,
  applies_to_clients: false,
  applies_to_opportunities: false,
  applies_to_policies: false,
  is_active: true,
  display_order: 0,
  optionsText: ""
};

export default function CustomFields() {
  usePageTitle("Custom Fields");

  const [customFields, setCustomFields] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadCustomFields();
  }, []);

  const getToken = () => localStorage.getItem("token");

  const loadCustomFields = async () => {
    try {
      setLoading(true);

      const res = await fetch(`${API_BASE_URL}/api/custom-fields/definitions`, {
        headers: {
          Authorization: `Bearer ${getToken()}`
        }
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "Failed to load custom fields");
      }

      setCustomFields(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Load custom fields error:", err);
      alert(err.message || "Failed to load custom fields.");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
  };

  const buildPayload = () => {
    const options = form.optionsText
      .split("\n")
      .map((option) => option.trim())
      .filter(Boolean)
      .map((option) => ({ label: option }));

    return {
      label: form.label.trim(),
      field_type: form.field_type,
      applies_to_leads: form.applies_to_leads,
      applies_to_clients: form.applies_to_clients,
      applies_to_opportunities: form.applies_to_opportunities,
      applies_to_policies: form.applies_to_policies,
      is_active: form.is_active,
      display_order: Number(form.display_order || 0),
      options: form.field_type === "dropdown" ? options : []
    };
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      setSaving(true);

      const payload = buildPayload();

      if (!payload.label) {
        alert("Field label is required.");
        return;
      }

      if (
        !payload.applies_to_leads &&
        !payload.applies_to_clients &&
        !payload.applies_to_opportunities &&
        !payload.applies_to_policies
      ) {
        alert("Select at least one area where this field applies.");
        return;
      }

      if (payload.field_type === "dropdown" && payload.options.length === 0) {
        alert("Dropdown fields require at least one option.");
        return;
      }

      const url = editingId
        ? `${API_BASE_URL}/api/custom-fields/definitions/${editingId}`
        : `${API_BASE_URL}/api/custom-fields/definitions`;

      const res = await fetch(url, {
        method: editingId ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`
        },
        body: JSON.stringify(payload)
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "Failed to save custom field");
      }

      resetForm();
      loadCustomFields();
    } catch (err) {
      console.error("Save custom field error:", err);
      alert(err.message || "Failed to save custom field.");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (field) => {
    setEditingId(field.id);

    setForm({
      label: field.label || "",
      field_type: field.field_type || "text",
      applies_to_leads: Boolean(field.applies_to_leads),
      applies_to_clients: Boolean(field.applies_to_clients),
      applies_to_opportunities: Boolean(field.applies_to_opportunities),
      applies_to_policies: Boolean(field.applies_to_policies),
      is_active: Boolean(field.is_active),
      display_order: field.display_order || 0,
      optionsText: Array.isArray(field.options)
        ? field.options.map((option) => option.option_label).join("\n")
        : ""
    });

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (field) => {
    const confirmed = window.confirm(
      `Delete "${field.label}"?\n\nThis will remove the field and its stored values.`
    );

    if (!confirmed) return;

    try {
      const res = await fetch(`${API_BASE_URL}/api/custom-fields/definitions/${field.id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${getToken()}`
        }
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "Failed to delete custom field");
      }

      loadCustomFields();

      if (editingId === field.id) {
        resetForm();
      }
    } catch (err) {
      console.error("Delete custom field error:", err);
      alert(err.message || "Failed to delete custom field.");
    }
  };

  const getAppliesToLabel = (field) => {
    const areas = [];

    if (field.applies_to_leads) areas.push("Leads");
    if (field.applies_to_clients) areas.push("Clients");
    if (field.applies_to_opportunities) areas.push("Opportunities");
    if (field.applies_to_policies) areas.push("Policies");

    return areas.join(", ") || "None";
  };

  return (
    <>
      <Navbar />
      <Topbar />

      <div className="main-content sales-page">
        <div className="sales-hero sales-profile-hero">
          <div>
            <p className="sales-eyebrow">Data & Customization</p>
            <h1 className="sales-title">Custom Fields</h1>
            <p className="sales-subtitle">
              Create account-wide fields for leads, clients, opportunities, and policies.
            </p>
          </div>

          <div className="sales-header-actions">
            <Link className="sales-secondary-btn" to="/settings?tab=Data%20%26%20Customization">
              Back to Data & Customization
            </Link>
          </div>
        </div>

        <div className="sales-profile-grid">
          <form className="sales-section-card sales-section-lead" onSubmit={handleSubmit}>
            <div className="sales-section-head">
              <h2>{editingId ? "Edit Custom Field" : "Add Custom Field"}</h2>
              <p>Choose the field type and where this custom field should appear.</p>
            </div>

            <div className="sales-form-fields">
              <div className="sales-field">
                <label>Field Label</label>
                <input
                  value={form.label}
                  onChange={(e) => handleChange("label", e.target.value)}
                  placeholder="Example: Household Income"
                />
              </div>

              <div className="sales-field">
                <label>Field Type</label>
                <select
                  value={form.field_type}
                  onChange={(e) => handleChange("field_type", e.target.value)}
                >
                  <option value="text">Text</option>
                  <option value="date">Date</option>
                  <option value="dropdown">Dropdown</option>
                </select>
              </div>

              <div className="sales-field">
                <label>Display Order</label>
                <input
                  type="number"
                  value={form.display_order}
                  onChange={(e) => handleChange("display_order", e.target.value)}
                />
              </div>

              <div className="sales-field">
                <label>Status</label>
                <select
                  value={form.is_active ? "active" : "inactive"}
                  onChange={(e) => handleChange("is_active", e.target.value === "active")}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              <div className="sales-field sales-field-full">
                <label>Show This Field On</label>

                <div className="settings-inline-actions">
                  <label>
                    <input
                      type="checkbox"
                      checked={form.applies_to_leads}
                      onChange={(e) => handleChange("applies_to_leads", e.target.checked)}
                    />{" "}
                    Leads
                  </label>

                  <label>
                    <input
                      type="checkbox"
                      checked={form.applies_to_clients}
                      onChange={(e) => handleChange("applies_to_clients", e.target.checked)}
                    />{" "}
                    Clients
                  </label>

                  <label>
                    <input
                      type="checkbox"
                      checked={form.applies_to_opportunities}
                      onChange={(e) => handleChange("applies_to_opportunities", e.target.checked)}
                    />{" "}
                    Opportunities
                  </label>

                  <label>
                    <input
                      type="checkbox"
                      checked={form.applies_to_policies}
                      onChange={(e) => handleChange("applies_to_policies", e.target.checked)}
                    />{" "}
                    Policies
                  </label>
                </div>
              </div>

              {form.field_type === "dropdown" && (
                <div className="sales-field sales-field-full">
                  <label>Dropdown Options</label>
                  <textarea
                    className="sales-note-textarea"
                    value={form.optionsText}
                    onChange={(e) => handleChange("optionsText", e.target.value)}
                    placeholder={"Enter one option per line\nExample:\nOption A\nOption B\nOption C"}
                  />
                </div>
              )}
            </div>

            <div className="settings-inline-actions" style={{ marginTop: "14px" }}>
              <button className="sales-primary-btn" type="submit" disabled={saving}>
                {saving ? "Saving..." : editingId ? "Save Custom Field" : "Add Custom Field"}
              </button>

              {editingId && (
                <button className="sales-secondary-btn" type="button" onClick={resetForm}>
                  Cancel Editing
                </button>
              )}
            </div>
          </form>

          <div className="sales-table-card">
            <div className="sales-table-header">
              <div>
                <h2>Custom Field Library</h2>
                <p>{customFields.length} custom field{customFields.length === 1 ? "" : "s"}</p>
              </div>
            </div>

            <div className="sales-table-wrap">
              <table className="sales-table">
                <thead>
                  <tr>
                    <th>Field</th>
                    <th>Type</th>
                    <th>Applies To</th>
                    <th>Status</th>
                    <th style={{ width: "170px" }}>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="5" className="sales-empty-cell">
                        Loading custom fields...
                      </td>
                    </tr>
                  ) : customFields.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="sales-empty-cell">
                        No custom fields have been created yet.
                      </td>
                    </tr>
                  ) : (
                    customFields.map((field) => (
                      <tr key={field.id}>
                        <td>
                          <div className="sales-primary-cell">
                            <div className="sales-name">{field.label}</div>
                            <div className="sales-inline-muted">{field.field_key}</div>
                          </div>
                        </td>

                        <td>{field.field_type}</td>
                        <td>{getAppliesToLabel(field)}</td>
                        <td>{field.is_active ? "Active" : "Inactive"}</td>

                        <td>
                          <div className="settings-inline-actions">
                            <button
                              type="button"
                              className="sales-secondary-btn"
                              onClick={() => handleEdit(field)}
                            >
                              Edit
                            </button>

                            <button
                              type="button"
                              className="sales-danger-btn"
                              onClick={() => handleDelete(field)}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}